import React from 'react'

import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ConfirmationCodeInput as DesignSystemConfirmationCodeInput, FieldLabel } from '@cash-design-system/react'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'

import { getConfirmationCodeFormat } from '../util'
import { TextInputElementProps } from '../util/types'
import { FieldController } from './FieldController/FieldController'
import { FormMaskedInput } from './FormMaskedInput'

type Props = {
  showLabel?: boolean
} & TextInputElementProps

const InnerConfirmationCodeInput: React.FC<Props> = ({ name, defaultValue, validate, ...props }) => {
  const { t } = useTranslation()
  const { watch } = useFormContext()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const watchCode = watch('code') || ''
  const codeFormat = getConfirmationCodeFormat(watchCode)

  if (isArcadeMigrationEnabled) {
    return (
      <FieldController
        FieldComponent={DesignSystemConfirmationCodeInput}
        componentProps={{
          autoFocus: true,
          maxDigits: 12,
          placeholder: t('Code', { context: 'placeholder text for form field asking for confirmation code' }),
          ...props,
        }}
        defaultValue={defaultValue}
        name={name || 'code'}
        rules={{
          required: {
            value: true,
            message: t('Enter a confirmation code.', {
              context: 'error message for not entering a confirmation code',
            }),
          },
          validate,
        }}
      />
    )
  }

  return (
    <FormMaskedInput
      autoFocus
      autoComplete="one-time-code"
      name="code"
      placeholder={t('Code', { context: 'placeholder text for form field asking for confirmation code' })}
      format={codeFormat}
      rules={{
        required: {
          value: true,
          message: t('Enter a confirmation code.', { context: 'error message for not entering a confirmation code' }),
        },
      }}
    />
  )
}
export function ConfirmationCodeInput({ showLabel = true, ...props }: Props): React.ReactElement {
  const { t } = useTranslation()

  if (showLabel) {
    return (
      <FieldLabel label={t('Confirmation code', { context: 'label for form field asking for confirmation code' })}>
        <InnerConfirmationCodeInput {...props} />
      </FieldLabel>
    )
  }

  return <InnerConfirmationCodeInput {...props} />
}
